'use client';

import styles from './AdminChatList.module.css';
import type { AdminChatListProps } from './AdminChatList.types';
import { Input, Flex, ButtonGroup, Button } from '@/_components';
import { ChatItem } from './components/ChatItem';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { Conversation, Message } from '@/_types/models/conversations';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import {
  useContext,
  useMemo,
  useState,
  type FC,
  type ChangeEvent,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { getAllConversationNames } from '@/_utils/conversations';
import { UserContext } from '@/_contexts/UserProvider';
import { ChatItemLoading } from './components/ChatItemLoading';
import { useDebounce } from '@/_hooks/useDebounce';
import { findConversations, getV2Conversations } from '@/_api/v2/conversations';

const DEFAULT_PER_PAGE = 10;
const FILTER_DEBOUNCE = 640 as const;

export const AdminChatList: FC<AdminChatListProps> = ({
  isAdvisor,
  shouldShowAllConversations,
  showUnreadOnly,
}) => {
  const [isLimiting, setLimiting] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>('');
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false);
  const [foundConversations, setFoundConversations] = useState<
    Array<Conversation>
  >([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  const [allLoadedConversations, setAllLoadedConversations] = useState<
    Array<Conversation>
  >([]);

  const chatListRef = useRef<HTMLDivElement>(null);

  const searchDebounce: string = useDebounce<string>(filter, FILTER_DEBOUNCE);

  const { user, token } = useContext(UserContext);
  const { isConversationsLoading, selectedCompanyConversations } =
    useContext(CompanyContext);
  const { conversations, conversationIdNumber, isLoading } = useContext(
    AdminConversationsContext,
  );

  // Load more conversations when scrolling to bottom
  const loadMoreConversations = useCallback(async () => {
    if (
      !shouldShowAllConversations ||
      isLoadingMore ||
      !hasMorePages ||
      !token
    ) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const newConversations = await getV2Conversations({
        token,
        page: nextPage,
        perPage: DEFAULT_PER_PAGE,
      });

      if (newConversations && newConversations.length > 0) {
        setAllLoadedConversations((prev) => [...prev, ...newConversations]);
        setCurrentPage(nextPage);

        if (newConversations.length < DEFAULT_PER_PAGE) {
          setHasMorePages(false);
        }
      } else {
        setHasMorePages(false);
      }
    } catch (error) {
      console.error('Error loading more conversations:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    shouldShowAllConversations,
    isLoadingMore,
    hasMorePages,
    token,
    currentPage,
  ]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!chatListRef.current || !shouldShowAllConversations) return;

    const { scrollTop, scrollHeight, clientHeight } = chatListRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

    if (isNearBottom && hasMorePages && !isLoadingMore) {
      loadMoreConversations();
    }
  }, [
    shouldShowAllConversations,
    hasMorePages,
    isLoadingMore,
    loadMoreConversations,
  ]);

  const standaloneConversations: Array<Conversation> = useMemo(() => {
    if (shouldShowAllConversations) {
      // Use our paginated conversations when showing all
      return allLoadedConversations.length > 0
        ? allLoadedConversations
        : conversations;
    }

    return isAdvisor
      ? isConversationsLoading
        ? []
        : selectedCompanyConversations
      : conversations;
  }, [
    shouldShowAllConversations,
    allLoadedConversations,
    conversations,
    isAdvisor,
    isConversationsLoading,
    selectedCompanyConversations,
  ]);

  const filteredConversations: Array<Conversation> = useMemo(() => {
    return standaloneConversations?.filter((c) => {
      if (!showUnreadOnly) return true;

      const unreadCount: number =
        c.unseen_messages?.filter(
          (um: Message) =>
            !um?.is_log && um?.unseen_user_ids?.includes?.(user?.id ?? -1),
        )?.length ?? 0;

      return unreadCount > 0;
    });
  }, [standaloneConversations, showUnreadOnly, user]);

  const isLimited: boolean = useMemo(() => {
    return Boolean(
      !shouldShowAllConversations &&
        !isLoading &&
        isLimiting &&
        isAdvisor &&
        filteredConversations?.length > 1,
    );
  }, [
    isAdvisor,
    isLimiting,
    isLoading,
    shouldShowAllConversations,
    filteredConversations?.length,
  ]);

  const allConversations: Array<Conversation> = useMemo(() => {
    if (!filteredConversations?.length || isSearchLoading) return [];
    if (!!searchDebounce) return foundConversations;

    const hasActiveConversation: boolean = filteredConversations.some(
      (c: Conversation): boolean => c.id === conversationIdNumber,
    );

    if (typeof conversationIdNumber === 'number' && !hasActiveConversation) {
      console.error('ConversationItemMissingError');

      return [];
    }

    return isLimited
      ? filteredConversations?.filter((c) => c.id === conversationIdNumber)
      : filteredConversations;
  }, [
    searchDebounce,
    isSearchLoading,
    foundConversations,
    isLimited,
    filteredConversations,
    conversationIdNumber,
  ]);

  const _onSearch = useCallback(
    async (newSearchTerm: string) => {
      if (!newSearchTerm) {
        setSearchLoading(false);
        setFoundConversations([]);

        return;
      }

      setSearchLoading(true);

      try {
        const conversations = await findConversations({
          search: newSearchTerm,
          token,
        });

        setFoundConversations(conversations);
      } catch (error) {
        console.error(error);
      } finally {
        setSearchLoading(false);
      }
    },
    [token],
  );

  const onRemoveEntityFilter = () => setLimiting(false);
  const onClearFilter = () => setFilter('');

  const onFilterChange = async (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  // Set up scroll listener
  useEffect(() => {
    const chatListElement = chatListRef.current;
    if (!chatListElement || !shouldShowAllConversations) return;

    chatListElement.addEventListener('scroll', handleScroll);
    return () => chatListElement.removeEventListener('scroll', handleScroll);
  }, [handleScroll, shouldShowAllConversations]);

  // Initialize loaded conversations when the main conversations change
  useEffect(() => {
    if (shouldShowAllConversations && conversations.length > 0) {
      setAllLoadedConversations(conversations);
      setCurrentPage(1);
      setHasMorePages(true);
    }
  }, [conversations, shouldShowAllConversations]);

  useEffect(() => {
    _onSearch(searchDebounce);
  }, [searchDebounce, _onSearch]);

  useEffect(() => {
    if (
      shouldShowAllConversations &&
      showUnreadOnly &&
      hasMorePages &&
      !isLoadingMore &&
      filteredConversations &&
      filteredConversations.length < DEFAULT_PER_PAGE &&
      filteredConversations.length >= 0
    ) {
      loadMoreConversations();
    }
  }, [
    shouldShowAllConversations,
    filteredConversations,
    showUnreadOnly,
    hasMorePages,
    isLoadingMore,
    loadMoreConversations,
  ]);

  if (!isAdvisor) return null;

  return (
    <Flex
      isColumn={true}
      gap={6}
      padding={6}
      className={styles.chatList}
      ref={chatListRef}
    >
      <Flex isColumn={true} gap={2}>
        {shouldShowAllConversations && (
          <Flex className={styles.chatList__search}>
            <Input
              label="Filter"
              placeholder="Filter"
              autoComplete="off"
              isLabelHidden={true}
              value={filter}
              iconRight={filter ? 'x' : undefined}
              onRightClick={filter ? onClearFilter : undefined}
              onChange={onFilterChange}
              type="text"
            />
          </Flex>
        )}
        {(isLoading || isSearchLoading) && <ChatItemLoading />}
        {allConversations?.map((conversation, key) => {
          const isActive: boolean = conversationIdNumber === conversation.id;
          const conversationNames: Array<string> = getAllConversationNames(
            conversation,
            isAdvisor,
          );

          const isGroup: boolean = conversation?.user_ids?.length > 1;

          return (
            <ChatItem
              key={key}
              isAdvisor={isAdvisor}
              conversation={conversation}
              name={conversationNames?.join(', ')}
              type={!isAdvisor ? 'firm' : isGroup ? 'group' : 'user'}
              isActive={isActive}
            />
          );
        })}
        {isLoadingMore && shouldShowAllConversations && <ChatItemLoading />}
      </Flex>
      {isLimited && (
        <ButtonGroup align="center">
          <Button
            intent="secondary"
            iconLeft="chats"
            value="Show All"
            onClick={onRemoveEntityFilter}
            size="small"
          />
        </ButtonGroup>
      )}
    </Flex>
  );
};
